<template>
  <div class="meetingTimeInfo">
    <section class="starts" v-click-outside="closeStartTimes">
      <span class="label" @click="closeStartTimes">Starts</span>
      <span class="date" @click="closeStartTimes">{{ startDateString }}</span>
      <input
        type="time"
        v-model="localStartTimeString"
        step="300"
        ref="startTimeInput"
        @change="interactedWithStart = true"
        @focus="showStartTimes = true"
        :class="[error ? 'hasError' : '']"
      />
      <transition name="basic">
        <div class="picker" v-if="showStartTimes">
          <ul class="minutes">
            <li v-for="time in startTimesList" :class="[time.value === localStartTimeString ? 'current' : '']" :key="time.label">
              <button @click="setStart(time.value)">{{ time.label }}</button>
            </li>
          </ul>
        </div>
      </transition>
    </section>
    <transition name="basic">
      <p class="error" v-if="error">{{ error }}</p>
    </transition>
    <section class="ends" v-click-outside="closeEndTimes">
      <span class="label" @click="closeEndTimes">Ends</span>
      <span class="date" @click="closeEndTimes">{{ endDateString }}</span>
      <input type="time" v-model="localEndTimeString" step="300" ref="endTimeInput" @change="interactedWithEnd = true" @focus="showEndTimes = true" />
      <transition name="basic">
        <div class="picker" v-if="showEndTimes">
          <ul class="minutes">
            <li v-for="time in endTimesList" :class="[time.label === localEndTimeString ? 'current' : '']" :key="time.label">
              <button @click="setEnd(time.value)">{{ time.label }}</button>
            </li>
          </ul>
        </div>
      </transition>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import { useUsersStore } from '@/store/pinia/users';
import { usePhaseStore } from '@/store/pinia/phase';
import { useTimeStore } from '@/store/pinia/time';
import _ from 'underscore';

const minutesOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const phaseStore = usePhaseStore();
const usersStore = useUsersStore();
const timeStore = useTimeStore();

const { $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;

const interactedWithStart = ref(false);
const interactedWithEnd = ref(false);
const localStartTimeString = ref(0);
const localEndTimeString = ref(0);
const localEndHours = ref(0);
const localEndMinutes = ref(0);
const startTimeInput = ref(null);
const endTimeInput = ref(null);

const showStartTimes = ref(false);
const showEndTimes = ref(false);
const error = ref(false);

const { currentPhase } = storeToRefs(phaseStore);
const { meetingStart: startTime, meetingEnd: endTime, meetingLength } = storeToRefs(timeStore);
const { usersOnline } = storeToRefs(usersStore);

const startTimeString = computed(() => Intl.DateTimeFormat('en-uk', { timeStyle: 'short', hourCycle: 'h24' }).format(startTime.value));
const startDateString = computed(() => Intl.DateTimeFormat('en-uk', { weekday: 'short', day: 'numeric', month: 'short' }).format(startTime.value));
const endTimeString = computed(() => Intl.DateTimeFormat('en-uk', { timeStyle: 'short', hourCycle: 'h24' }).format(endTime.value));
const endDateString = computed(() => Intl.DateTimeFormat('en-uk', { weekday: 'short', day: 'numeric', month: 'short' }).format(endTime.value));

const localStartTime = computed(() => {
  if (localStartTimeString.value) {
    let newStart = new Date(startTime.value);
    let [hours, minutes] = localStartTimeString.value.split(':');
    newStart.setHours(hours);
    if (minutes) {
      newStart.setMinutes(minutes);
    }
    return newStart;
  } else {
    return new Date();
  }
});

const localEndTime = computed(() => {
  if (localEndTimeString.value) {
    let newEnd = new Date(startTime.value);
    let [hours, minutes] = localEndTimeString.value.split(':');
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    newEnd.setHours(hours);
    if (minutes) {
      newEnd.setMinutes(minutes);
    }
    if (newEnd <= localStartTime.value) {
      newEnd.setHours(newEnd.getHours() + 24);
    }
    return newEnd;
  } else {
    return new Date();
  }
});

function createTimeLabels(m, timeString) {
  let label = String(m);
  label = label.length < 2 ? '0' + label : label;
  return {
    value: timeString ? `${timeString.split(':')[0]}:${label}` : m,
    label,
  };
}

const startTimesList = computed(() => minutesOptions.map((m) => createTimeLabels(m, startTimeString.value)));
const endTimesList = computed(() => minutesOptions.map((m) => createTimeLabels(m, endTimeString.value)));

onMounted(() => {
  // Check if meeting is in the past
  let now = new Date();
  if (startTime.value < now && currentPhase.value === 'lobby' && usersOnline.value.length === 1) {
    let newEnd = new Date();
    newEnd.setTime(now.getTime() + meetingLength.value);
    // If so, update the time to be based on now
    broadcastTime({
      start: now,
      end: newEnd,
    });
  } else {
    updateFields();
  }
});

watch(startTime, updateFields);
watch(endTime, updateFields);

watch(localStartTimeString, () => {
  let newEnd = new Date(localStartTime.value);

  let now = new Date();
  if (localStartTime.value < now && interactedWithStart.value) {
    error.value = 'Start time must be later than current time';
  } else {
    error.value = false;
  }

  if (!interactedWithEnd.value) {
    newEnd.setTime(newEnd.getTime() + meetingLength.value);
    localEndHours.value = newEnd.getHours();
    localEndMinutes.value = newEnd.getMinutes();
    if (newEnd <= localStartTime.value) {
      newEnd.setHours(newEnd.getHours() + 24);
    }
  } else {
    newEnd.setHours(localEndTime.value.getHours());
    newEnd.setMinutes(localEndTime.value.getMinutes());
    if (newEnd <= localStartTime.value) {
      newEnd.setHours(newEnd.getHours() + 24);
    }
  }

  if (!error.value) {
    broadcastTime({ start: localStartTime.value, end: newEnd });
  }
});
watch(localEndTimeString, () => {
  let newEnd = new Date(localStartTime.value);
  newEnd.setHours(localEndTime.value.getHours());
  newEnd.setMinutes(localEndTime.value.getMinutes());
  if (newEnd <= localStartTime.value) {
    newEnd.setHours(newEnd.getHours() + 24);
  }
  broadcastTime({ start: localStartTime.value, end: newEnd });
});

function broadcastTimeRaw({ start, end }) {
  nextTick(() => {
    if (currentPhase.value === 'lobby') {
      $meetingmanager.updateMeeting({
        starts_on: start.getTime() / 1000,
        ends_on: end.getTime() / 1000,
        trigger: 'scheduler',
      });
    }
  });
}
const broadcastTime = _.debounce(broadcastTimeRaw, 600);

function updateFields() {
  localStartTimeString.value = Intl.DateTimeFormat('en-uk', { timeStyle: 'short', hourCycle: 'h24' }).format(startTime.value);
  localEndTimeString.value = Intl.DateTimeFormat('en-uk', { timeStyle: 'short', hourCycle: 'h24' }).format(endTime.value);
}
function setStart(m) {
  localStartTimeString.value = m;
  showStartTimes.value = false;
  interactedWithStart.value = true;
}
function setEnd(m) {
  localEndTimeString.value = m;
  showEndTimes.value = false;
  interactedWithEnd.value = true;
}

function closeStartTimes() {
  showStartTimes.value = false;
}
function closeEndTimes() {
  showEndTimes.value = false;
}
</script>
<style lang="scss" scoped>
.meetingTimeInfo {
  padding: rem(25px) rem(30px) rem(20px) rem(30px);
  position: absolute;
  bottom: rem(80px);
  width: 100%;
  border-top: 1px solid var(--c__edges);
  background: var(--c__bg);
  .error {
    margin-right: rem(-20px);
    color: var(--c__warn);
    position: absolute;
    left: rem(30px);
    top: rem(43px);
  }
  .starts,
  .ends {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }
  .starts {
    margin: 0;
  }
  .ends {
    margin: rem(13px) 0 0 0;
  }

  .date {
    margin-right: auto;
  }

  .label {
    font-weight: bold;
    flex: 0 0 rem(85px);
  }
  input[type='time'] {
    border: none;
    width: 4.75em;
    color: var(--c__text);
    @include triggered {
      outline: none;
      outline-color: transparent;
      outline-style: none;
    }
    &.hasError {
      color: var(--c__warn);
    }
  }
  input[type='time']::-webkit-calendar-picker-indicator {
    display: none;
  }

  .picker {
    position: absolute;
    bottom: 2em;
    right: 1em;
    background: white;
    //width: 7em;
    text-align: center;
    border: 1px solid var(--c__edges);
    padding: 0.5em 0;
    border-radius: 0.5em;
    box-shadow: var(--shadow);
    display: flex;
    ul {
      list-style: none;
    }
    .hours {
      width: 5em;
      column-count: 2;
      column-gap: 0;
      padding-right: 0.5em;
      border-right: 1px solid var(--c__edges);
      li {
        display: inline-block;
      }
    }
    .minutes {
      width: 3em;
    }
    button {
      width: 100%;
      @include triggered {
        background: var(--c__accent);
        color: var(--c__bg);
      }
    }
    .current button {
      background: var(--c__accent);
      color: var(--c__bg);
    }
  }
}
</style>
